import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { MessageContext } from "utils/context/messageContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAppNameKey } from "utils/applicationNames";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const MessagesTable = ({ messages }) => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const { setActiveMessage } = React.useContext(MessageContext);
  const navigate = useNavigate();

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - messages.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="custom pagination table">
          <TableHead sx={{ backgroundColor: "#d8e5f0", color: "white" }}>
            <TableRow>
              <TableCell>{t("application")}</TableCell>
              <TableCell align="left">{t("subject")}</TableCell>
              <TableCell align="left">{t("dateTime")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.length <= 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {t("noMessagesAvailable")}
                </TableCell>
              </TableRow>
            )}
            {(rowsPerPage > 0
              ? messages.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
              : messages
            ).map((msg, index) => (
              <TableRow
                hover
                sx={{
                  backgroundColor: msg.is_read ? "#f0f0f0" : "#ffffff",
                  "&:hover": {
                    backgroundColor: "blue",
                    cursor: "pointer",
                  },
                }}
                onClick={() => {
                  setActiveMessage(msg);
                  navigate(`/message/${msg.messageid}`);
                }}
                key={index}
              >
                <TableCell component="th" scope="row" sx={{ fontWeight: msg.is_read ? "normal" : "bold" }}>
                {t(getAppNameKey(msg.application_name) ?? msg.application_name)}
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: msg.is_read ? "normal" : "bold" }}>{msg.subject}</TableCell>
                <TableCell align="left" sx={{ fontWeight: msg.is_read ? "normal" : "bold" }}>{msg.date}</TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
          {messages.length > 0 && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: t("all"), value: -1 }]}
                  colSpan={3}
                  count={messages.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  labelRowsPerPage={t("table.rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${t('table.displayed_rows', { from, to, count: count !== -1 ? count : `more than ${to}` })}`
                  }
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MessagesTable;
